import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface StoryMilestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface CompanyStoryProps {
  milestones: StoryMilestone[];
}

const CompanyStory: React.FC<CompanyStoryProps> = ({ milestones }) => {
  const { t } = useLanguage();
  
  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="BookOpenIcon" size={20} className="text-accent" />
            <span className="text-sm font-cta font-semibold text-accent">{t('about.story.badge')}</span>
          </div>
          
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            {t('about.story.title')}
          </h2>
          
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            {t('about.story.description')}
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-brand-primary via-accent to-brand-secondary" />
            
            {milestones.map((milestone, index) => {
              const translatedMilestone = t(`about.story.milestones.${index}`);
              const milestoneData = typeof translatedMilestone === 'object' ? translatedMilestone : milestone;
              
              return (
              <div
                key={index}
                className={`relative mb-12 lg:mb-16 ${
                  index % 2 === 0 ? 'lg:pr-1/2' : 'lg:pl-1/2 lg:text-right'
                }`}
              >
                <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12 lg:ml-auto'}`}>
                  <div className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 p-6 lg:p-8 border border-border">
                    <div className={`flex items-center space-x-3 mb-4 ${index % 2 === 0 ? '' : 'lg:flex-row-reverse lg:space-x-reverse'}`}>
                      <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name={milestone.icon as any} size={24} className="text-white" variant="solid" />
                      </div>
                      <span className="font-headline font-bold text-2xl text-brand-primary">
                        {milestoneData.year || milestone.year}
                      </span>
                    </div>
                    
                    <h3 className="font-headline font-bold text-xl text-primary mb-3">
                      {milestoneData.title || milestone.title}
                    </h3>
                    
                    <p className="font-body text-muted-foreground leading-relaxed">
                      {milestoneData.description || milestone.description}
                    </p>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 top-8 transform -translate-x-1/2 w-4 h-4 bg-accent rounded-full border-4 border-background shadow-card" />
              </div>
            )})}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyStory;